/**
 * Una conversación (`/activa/oficial/conversaciones/:id`): la ficha, el hilo y la caja para contestar
 * (§15.3). El `:id` es el de la conversación, nunca el teléfono.
 *
 * `key={id}`: con React Router, cuando cambia sólo el `:id` la instancia se REUSA. Sin la `key` seguían
 * puestos el borrador de la caja, su clave, la tarjeta del 409, las variables de la reapertura y el motivo
 * de la baja, y «Enviar» le mandaba a Pedro lo que el operador escribió para Juana. Con la `key` todo se
 * vuelve a montar por conversación, y lo que llega tarde de la anterior se descarta (el componente viejo
 * ya no existe; además cada pedido lleva `seq` y un flag `vivo`).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { conversacionesApi } from "../../conversacionesApi";
import type { ConversacionDetalle, Hilo as HiloDatos, ItemHilo } from "../../types.conversaciones";
import { useCuentaOficial } from "../../contexto";
import { usePolling } from "../../hooks/usePolling";
import { CajaError, Esqueleto, llamar } from "../../components/base/comun";
import type { Fallo } from "../../components/base/comun";
import { textoError } from "../../logic/respuesta";
import {
  ESTADOS_EN_CURSO, avisarCambioConversaciones, desfaseMs, esComputadora, fusionarItems, hrefConversacion, hrefLista, ordenarItems,
} from "../../logic/conversaciones";
import { FichaConversacion } from "../../components/conversaciones/FichaConversacion";
import { Hilo } from "../../components/conversaciones/Hilo";
import { CajaRespuesta } from "../../components/conversaciones/CajaRespuesta";
import { ReabrirConPlantilla } from "../../components/conversaciones/ReabrirConPlantilla";
import { SimularEnHilo } from "../../components/conversaciones/SimularEnHilo";
import { useEsEquipoDeenex } from "../../../campana-nueva/hooks/useEsEquipoDeenex";

/** Rápido sólo mientras se confirma un envío propio: con el teléfono apagado, `enviado` dura horas. */
const REFRESCO_RAPIDO_MS = 5_000;
const REFRESCO_MS = 20_000;
const VENTANA_RAPIDA_MS = 2 * 60_000;
/** «Cerca del final»: si el último mensaje estaba a menos de esto del borde de abajo, un refresco baja. */
const CERCA_DEL_FINAL_PX = 80;
/** «Pegado al final»: después de bajar, el último mensaje queda justo arriba del borde (con el margen del hilo). Una
 *  foto que termina de cargar vuelve a bajar sólo si el operador sigue ahí, no si subió unos px a leer. */
const PEGADO_AL_FINAL_PX = 8;

export default function ConversacionPage() {
  const { id = "" } = useParams();
  return <ContenidoConversacion key={id} id={id} />;
}

export function ContenidoConversacion({ id }: { id: string }) {
  const equipo = useEsEquipoDeenex();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const spRef = useRef(sp);
  spRef.current = sp;
  const { modo } = useCuentaOficial();

  const [conv, setConv] = useState<ConversacionDetalle | null>(null);
  const [items, setItems] = useState<Map<string, ItemHilo>>(() => new Map());
  // El cursor de la página MÁS VIEJA cargada: el refresco no lo toca.
  const [cursorAnteriores, setCursorAnteriores] = useState<string | null>(null);
  const [desfase, setDesfase] = useState(0);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [cargandoAnteriores, setCargandoAnteriores] = useState(false);
  const [errorAnteriores, setErrorAnteriores] = useState("");
  const [ultimoEnvioPropio, setUltimoEnvioPropio] = useState(0);
  const [textoParaCargar, setTextoParaCargar] = useState<{ texto: string; n: number } | null>(null);
  // Cada toque de «Reabrir con plantilla» (también con la tarjeta ya abierta) la vuelve a traer a la vista.
  const [pedidoReabrir, setPedidoReabrir] = useState(0);
  // El reloj de 30 s: la ventana, «faltan…» y el ritmo del refresco se recalculan solos.
  const [, setTic] = useState(0);

  const vivo = useRef(true);
  const seq = useRef(0);
  const convRef = useRef(conv);
  convRef.current = conv;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  /** Qué hacer con el scroll después del próximo render de los ítems. */
  const bajar = useRef<"no" | "fin">("no");
  const anclar = useRef<string | null>(null);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const finRef = useRef<HTMLDivElement>(null);
  const cajaRef = useRef<HTMLDivElement>(null);
  /** ¿El operador quedó abajo del todo? Lo pone el scroll al final y lo saca el operador si sube a leer. */
  const pegadoAlFinal = useRef(false);
  const reaperturaRef = useRef<HTMLDivElement>(null);
  /** La tarjeta de la reapertura se llevó a la vista mientras cargaba: al llegar las plantillas crece y se vuelve a acomodar. */
  const acomodarReapertura = useRef(false);
  const nCarga = useRef(0);

  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);
  useEffect(() => {
    const t = setInterval(() => setTic((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  /** Un fallo al leer: la conversación que se unió con otra lleva a ésa (sin dejar la vieja en el historial). */
  const aplicarFallo = useCallback((l: Fallo) => {
    const destino = l.extra?.fusionadaEn;
    if (l.code === "CONVERSACION_FUSIONADA" && typeof destino === "string" && destino) {
      navigate(hrefConversacion(destino, spRef.current), { replace: true });
      return;
    }
    setFallo(l);
  }, [navigate]);

  /** ¿El último mensaje está a la vista (a menos de `margen` px del borde de abajo)? */
  const cercaDelFinal = (margen = CERCA_DEL_FINAL_PX) => {
    const lista = ordenarItems(itemsRef.current);
    const ultimo = lista[lista.length - 1];
    const el = ultimo ? document.getElementById(`item-${ultimo.id}`) : null;
    if (!el) return true;
    const cont = contenedorRef.current;
    const scrollPropio = !!cont && cont.scrollHeight > cont.clientHeight + 1;
    const caja = cajaRef.current;
    // En el celular la caja va fija abajo y tapa su propio alto: el borde visible del hilo es el de arriba de ella, no
    // el de la pantalla. Con la pantalla, quien subía a leer lo que quedaba detrás de la caja volvía al final con
    // cada refresco (visto a 375x812: la caja mide 418 px).
    const borde = scrollPropio && cont
      ? cont.getBoundingClientRect().bottom
      : !esComputadora() && caja ? caja.getBoundingClientRect().top : window.innerHeight;
    return el.getBoundingClientRect().bottom - borde <= margen;
  };

  const aplicarHilo = useCallback((h: HiloDatos, primera: boolean) => {
    setConv(h.conversacion);
    setDesfase(desfaseMs(h.ahoraServidor, Date.now()));
    // Recién borrada (supresión o 12 meses): lo cargado antes tiene los datos de la persona, y el backend ya no lo
    // devuelve (la campaña que no citó) o lo devuelve vacío en otra página. Fusionar lo dejaba a la vista con la ficha
    // diciendo «se borraron». Sólo en el cambio: el refresco de una ya borrada sigue fusionando lo que se cargó después.
    // (`convRef` todavía tiene la conversación de antes: `setConv` se ve recién en el próximo render.)
    const recienBorrada = h.conversacion.anonimizada && !!convRef.current && !convRef.current.anonimizada;
    if (primera || recienBorrada) {
      if (primera) bajar.current = "fin";
      setItems(fusionarItems(new Map(), h.items));
      setCursorAnteriores(h.cursorAnteriores);
    } else {
      // La página más nueva se FUSIONA: reemplazar la lista borraba lo anterior que se había cargado.
      setItems((prev) => fusionarItems(prev, h.items));
    }
  }, []);

  const cargarInicial = useCallback(async () => {
    const mio = ++seq.current;
    setFallo(null);
    const l = await llamar<HiloDatos>(() => conversacionesApi.obtener(id));
    if (!vivo.current || mio !== seq.current) return;
    if (!l.ok) { aplicarFallo(l); return; }
    if (!l.data) { setFallo({ ok: false, status: 0, code: "SIN_RESPUESTA", error: "El servidor no devolvió la conversación.", extra: {} }); return; }
    aplicarHilo(l.data, true);
  }, [id, aplicarFallo, aplicarHilo]);

  useEffect(() => { void cargarInicial(); }, [cargarInicial]);

  /** Pide la página más nueva y la fusiona. Lanza si falla: el polling cuenta los errores. */
  const refrescar = useCallback(async (): Promise<void> => {
    const mio = ++seq.current;
    const l = await llamar<HiloDatos>(() => conversacionesApi.obtener(id));
    if (!vivo.current || mio !== seq.current) return;
    if (!l.ok || !l.data) {
      if (!l.ok && l.code === "CONVERSACION_FUSIONADA") { aplicarFallo(l); return; }
      throw new Error(l.ok ? "sin datos" : textoError(l));
    }
    const primera = !convRef.current;
    // En el celular, con la reapertura abierta el operador está en la tarjeta, abajo del hilo: bajar al final del hilo
    // la dejaba detrás de la caja fija en cada refresco. (Desde `xl` bajar mueve sólo el scroll del hilo.)
    const enLaTarjeta = !esComputadora() && spRef.current.get("accion") === "reabrir";
    if (!primera && bajar.current === "no" && !enLaTarjeta && cercaDelFinal()) bajar.current = "fin";
    aplicarHilo(l.data, primera);
  }, [id, aplicarFallo, aplicarHilo]);

  const recargar = useCallback(() => { void refrescar().catch(() => undefined); }, [refrescar]);
  const recargarYAvisar = useCallback(() => { recargar(); avisarCambioConversaciones(); }, [recargar]);

  /** Después de un envío propio: baja al final, refresca rápido un rato y avisa a la lista y al badge. */
  const alEnviar = useCallback(() => {
    setUltimoEnvioPropio(Date.now());
    bajar.current = "fin";
    recargarYAvisar();
  }, [recargarYAvisar]);

  const verAnteriores = async () => {
    if (!cursorAnteriores || cargandoAnteriores) return;
    const primero = ordenarItems(items)[0]?.id ?? null;
    setCargandoAnteriores(true);
    setErrorAnteriores("");
    const l = await llamar<HiloDatos>(() => conversacionesApi.obtener(id, { antesDe: cursorAnteriores }));
    if (!vivo.current) return;
    setCargandoAnteriores(false);
    if (!l.ok || !l.data) { setErrorAnteriores(l.ok ? "El servidor no devolvió los mensajes." : textoError(l)); return; }
    const pagina = l.data;
    // La posición se conserva: se vuelve al que era el primero. Quien lee arriba no es llevado al fondo.
    anclar.current = primero;
    bajar.current = "no";
    setItems((prev) => fusionarItems(prev, pagina.items));
    setCursorAnteriores(pagina.cursorAnteriores);
  };

  // Desde `xl` el alto del hilo es lo que dejan la ficha y la caja, y cambia sin que cambien los mensajes: un aviso que
  // llega después (el de modo simulado), un error en la caja, una tarjeta que se abre en la ficha. Al achicarse, el
  // scroll quedaba donde estaba y lo último quedaba tapado abajo (visto a 1440x900 en simulado: 121 px del último
  // mensaje). Si estaba al final, sigue al final; si el operador subió a leer, no se lo mueve.
  const hayConversacion = !!conv;
  useEffect(() => {
    const cont = contenedorRef.current;
    if (!cont || typeof ResizeObserver === "undefined") return;
    let alFinal = true;
    const alScroll = () => { alFinal = cont.scrollHeight - cont.scrollTop - cont.clientHeight <= CERCA_DEL_FINAL_PX; };
    const observador = new ResizeObserver(() => { if (alFinal && esComputadora()) cont.scrollTop = cont.scrollHeight; });
    cont.addEventListener("scroll", alScroll, { passive: true });
    observador.observe(cont);
    return () => { observador.disconnect(); cont.removeEventListener("scroll", alScroll); };
  }, [hayConversacion]);

  // Una foto, un sticker, un audio o un video terminan de cargar DESPUÉS de bajar al final: agrandan el hilo sin mover el
  // scroll, y lo último quedaba detrás de la caja (visto en el navegador a 1024x768 con una foto simulada). Si el operador
  // seguía abajo, se lo vuelve a llevar al final; si subió a leer, no se lo mueve. `load` no burbujea: se escucha en la
  // captura, sin pasarle nada a cada burbuja.
  useEffect(() => {
    const cont = contenedorRef.current;
    if (!cont) return;
    // Pegado de verdad, no «cerca»: quien subió 50 px a leer seguía dentro de los 80 del refresco, y la foto de una
    // burbuja de más arriba que terminaba de cargar lo devolvía al final.
    const alScroll = () => { pegadoAlFinal.current = cercaDelFinal(PEGADO_AL_FINAL_PX); };
    const alCargar = () => {
      if (!pegadoAlFinal.current || spRef.current.get("accion") === "reabrir") return;
      const hilo = esComputadora() ? contenedorRef.current : null;
      if (hilo) { hilo.scrollTop = hilo.scrollHeight; return; }
      const fin = finRef.current;
      if (!fin) return;
      fin.style.scrollMarginBottom = `${cajaRef.current?.offsetHeight ?? 0}px`;
      fin.scrollIntoView({ block: "end" });
    };
    document.addEventListener("scroll", alScroll, { capture: true, passive: true });
    cont.addEventListener("load", alCargar, true);
    cont.addEventListener("loadedmetadata", alCargar, true);
    return () => {
      document.removeEventListener("scroll", alScroll, { capture: true });
      cont.removeEventListener("load", alCargar, true);
      cont.removeEventListener("loadedmetadata", alCargar, true);
    };
  }, [hayConversacion]);

  // El scroll, después de pintar los ítems.
  useEffect(() => {
    // Desde `xl` el hilo tiene su propio scroll y se mueve SÓLO ése. `scrollIntoView` también mueve el contenedor de la
    // página (DashboardLayout): como la columna mide más que la pantalla, al abrir, en cada refresco con el hilo abajo,
    // después de cada envío y con «Ver mensajes anteriores» la página saltaba sola y la caja quedaba fuera de la
    // pantalla, aunque el operador estuviera escribiendo (visto a 1440x900).
    const cont = esComputadora() ? contenedorRef.current : null;
    if (anclar.current) {
      const el = document.getElementById(`item-${anclar.current}`);
      if (cont && el) cont.scrollTop += el.getBoundingClientRect().top - cont.getBoundingClientRect().top;
      else el?.scrollIntoView({ block: "start" });
      anclar.current = null;
      bajar.current = "no";
      return;
    }
    if (bajar.current === "no") return;
    bajar.current = "no";
    if (cont) {
      cont.scrollTop = cont.scrollHeight;
      pegadoAlFinal.current = true;
      // Con `?accion=reabrir` la tarjeta está al final del hilo, todavía cargando: al llegar las plantillas crece y
      // «Mandar plantilla» quedaba abajo del borde (a 1440x900, 193 px). Se vuelve a acomodar cuando termina.
      if (spRef.current.get("accion") === "reabrir") acomodarReapertura.current = true;
      return;
    }
    // En el celular, con `?accion=reabrir` (un link o F5) se muestra la tarjeta: el final del hilo queda ARRIBA de ella
    // y la caja fija la tapaba entera.
    if (spRef.current.get("accion") === "reabrir" && reaperturaRef.current) {
      acomodarReapertura.current = true;
      reaperturaRef.current.scrollIntoView({ block: "start" });
      return;
    }
    // La caja va fija abajo (sticky) y tapa su propio alto: el final del hilo se alinea ARRIBA de ella, así el último
    // mensaje y la caja quedan en pantalla. Un `scrollIntoView` sobre la caja no alcanzaba: pegada al borde ya cuenta
    // como visible y no mueve nada, y el último mensaje quedaba detrás (visto en el navegador).
    const fin = finRef.current;
    if (fin) fin.style.scrollMarginBottom = `${cajaRef.current?.offsetHeight ?? 0}px`;
    fin?.scrollIntoView({ block: "end" });
    pegadoAlFinal.current = true;
  }, [items]);

  // «Reabrir con plantilla» abre la tarjeta al final del hilo, arriba de la caja fija, que en el celular la tapaba:
  // parecía que no había pasado nada, y un segundo toque tampoco movía nada. Arriba (`start`) la caja no la tapa. Desde
  // `xl` la tarjeta está adentro del scroll del hilo y la caja no es fija: alcanza con mover lo justo, y se mueve sólo el
  // hilo. Se lleva enseguida (con la red lenta, algo tiene que moverse) y otra vez al llegar las plantillas: cargando
  // mide poco, y al crecer «Mandar plantilla» quedaba detrás de la caja.
  // Borrada, no hay a quién reabrirle: después de «Borrar los datos» la tarjeta seguía montada con lo que había traído
  // (el número completo y el nombre precargado en la variable), y con un F5 se volvía a abrir a pedir plantillas.
  const reabriendo = sp.get("accion") === "reabrir" && !conv?.anonimizada;
  const mostrarReapertura = useCallback(() => {
    reaperturaRef.current?.scrollIntoView({ block: esComputadora() ? "nearest" : "start" });
  }, []);
  useEffect(() => {
    if (!pedidoReabrir || !reabriendo) return;
    acomodarReapertura.current = true;
    mostrarReapertura();
  }, [pedidoReabrir, reabriendo, mostrarReapertura]);
  const alCargarReapertura = useCallback(() => {
    if (!acomodarReapertura.current) return;
    acomodarReapertura.current = false;
    mostrarReapertura();
  }, [mostrarReapertura]);

  const lista = useMemo(() => ordenarItems(items), [items]);
  // Borrada, el hilo va sin acciones: la supresión le deja a un envío a mano su estado y su código, y la burbuja fallida
  // seguía ofreciendo «Reabrir con plantilla» o «Volver a intentar». Tocarlo no hacía nada (la tarjeta y la caja ya no
  // se muestran): el error queda como constancia de lo que pasó.
  const anonimizada = !!conv?.anonimizada;
  const enElHilo = useMemo(
    () => (anonimizada ? lista.map((i) => (i.tipo === "manual" && i.error ? { ...i, error: { ...i.error, accion: "ninguna" as const } } : i)) : lista),
    [lista, anonimizada],
  );
  const ahora = new Date(Date.now() + desfase);
  const enCurso = lista.some((i) => i.tipo === "manual" && ESTADOS_EN_CURSO.has(i.estado));
  const rapido = enCurso && Date.now() - ultimoEnvioPropio < VENTANA_RAPIDA_MS;
  const poll = usePolling(refrescar, conv ? (rapido ? REFRESCO_RAPIDO_MS : REFRESCO_MS) : null, [id]);

  const abrirReapertura = () => {
    setSp((prev) => { const n = new URLSearchParams(prev); n.set("accion", "reabrir"); return n; });
    setPedidoReabrir((n) => n + 1);
  };

  if (!conv) {
    if (fallo) {
      if (fallo.status === 404) {
        return (
          <CajaError titulo="No encontramos esa conversación.">
            <Link to={hrefLista(sp)} className="mt-2 inline-block font-semibold underline">Volver a la lista</Link>
          </CajaError>
        );
      }
      return <CajaError titulo="No pudimos traer la conversación." detalle={textoError(fallo)} onReintentar={() => void cargarInicial()} />;
    }
    return <Esqueleto filas={4} alto="h-16" />;
  }

  // Desde `xl`, una columna del alto que le deja la bandeja: la ficha arriba, el hilo en el medio con su scroll (se queda
  // con lo que sobra, nunca menos de 8rem) y la caja abajo, que no se achica: siempre a la vista, sin mover la página.
  // Lo que puede crecer mucho (la ficha con una tarjeta abierta, el simulador) se achica con su propio scroll antes que
  // tapar la caja; la reapertura va al final del hilo, en su scroll. Todo scroll propio es `relative` (ver `Hilo`). El
  // `gap` y no `space-y`: el link de volver está oculto pero sumaba su margen.
  return (
    <div className="space-y-4 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col xl:gap-3 xl:space-y-0">
      <Link to={hrefLista(sp)} className="xl:hidden inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900">
        <ArrowLeft size={14} /> Conversaciones
      </Link>
      <div data-testid="ficha" className="xl:relative xl:min-h-0 xl:overflow-y-auto">
        <FichaConversacion conversacion={conv} onCambio={recargarYAvisar} />
      </div>
      <Hilo items={enElHilo} ahora={ahora} cursorAnteriores={cursorAnteriores} cargandoAnteriores={cargandoAnteriores}
        errorAnteriores={errorAnteriores} onAnteriores={() => void verAnteriores()} onReabrir={abrirReapertura}
        onReintentar={(texto) => { nCarga.current += 1; setTextoParaCargar({ texto, n: nCarga.current }); }}
        contenedorRef={contenedorRef} finRef={finRef}
        pie={reabriendo && (
          <div ref={reaperturaRef} data-testid="tarjeta-reabrir">
            <ReabrirConPlantilla conversacion={conv} ahora={ahora} onEnviado={alEnviar} onCargada={alCargarReapertura} />
          </div>
        )} />
      {poll.detenido && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-800 xl:shrink-0">
          <span>Dejamos de actualizar esta conversación porque el servidor no responde.</span>
          <button type="button" onClick={poll.reintentar} className="font-semibold underline">Reintentar</button>
        </div>
      )}
      <div ref={cajaRef} data-testid="caja-fija" className="sticky bottom-0 z-10 bg-slate-50/95 pb-1 pt-2 xl:static xl:shrink-0 xl:bg-transparent xl:p-0">
        <CajaRespuesta conversacion={conv} items={lista} ahora={ahora} onEnviado={alEnviar} onRechazo={recargar}
          onReabrir={abrirReapertura} textoParaCargar={textoParaCargar} />
      </div>
      {/* Sólo en una conversación simulada: en una real, el entrante simulado caía en OTRA conversación (la simulada del
          mismo número) y la atribución podía marcar como respondida una campaña real. */}
      {/* El simulador es para probar: lo usa el equipo (a una persona de un cliente el backend se lo niega). */}
      {modo === "simulated" && conv.simulado && equipo && (
        <div data-testid="simular" className="xl:relative xl:min-h-0 xl:overflow-y-auto">
          <SimularEnHilo conversacion={conv} onSimulado={recargarYAvisar} />
        </div>
      )}
    </div>
  );
}
